// Authenticity Promise — the store's money-backed guarantee on saree composition.
//
// Deliberately shaped on ShippingBar (same folder): a slim, full-width announcement strip that
// opens a BottomSheet with the full terms. Same type scale (11.5px/400), same vertical rhythm
// (py-[9px]), same bottom hairline, so it reads as a sibling of the strips already under the
// store header rather than something bolted on. CouponBar was built to the same template.
//
// THE STRIP ITSELF IS NOT CLICKABLE. The only tap target is the trailing ⓘ, matching ShippingBar
// directly above it exactly — same glyph, same 14px, same s-primary, same aria-label pattern.
//
// An earlier revision made the whole bar tappable, on the reasoning that the terms (45 days, NABL,
// the ₹2,000 cap) are the substance rather than an optional detail, so the way in should be hard
// to miss. That was overridden in favour of consistency: two adjacent strips that look identical
// must not behave differently, and a full-width target that opens a modal is a surprise next to
// one that does nothing. Consistency with the neighbour wins here — if the ⓘ proves too small to
// find, the fix is to make the affordance clearer on BOTH strips, not to make this one special.
//
// GROUND COLOUR: #F2F8F4, a pale green, chosen because the three tinted strips around it are
// already taken and adjacent — ShippingBar lavender (#F4F5FF), CouponBar cream (#FFF8E7), the
// win-back bar peach (#FEEFEB). A fourth warm pastel would merge with its neighbours into one
// undifferentiated block, which is exactly the failure CouponBar's header comment records. Green
// also carries the verification idiom the copy is making.
//
// NO SAREE GATE HERE, unlike the PDP badge. The claim says "on every saree" and this is a
// store-level surface with no product in scope, so there is nothing to gate on — and the home
// payload carries no per-product category anyway. The PDP gate lives in Utils/sareeGate.js.
//
// The modal is NOT defined here — see Components/AuthenticityPromiseSheet.jsx, shared with the
// PDP badge so the terms cannot differ by the screen you opened them from.

import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AuthenticityPromiseSheet from "../AuthenticityPromiseSheet";

// Same fallback string the other store-name consumers use (StoreFooter, PolicyTemplate,
// PageNotFound, AddressForm) — so a storefront that somehow renders before storeDetails resolves
// shows the same placeholder name everywhere rather than a different one per component.
const BRAND_FALLBACK = "The Crafts Archive";

export default function AuthenticityPromiseBar() {
  const { t } = useTranslation();
  const [sheet, setSheet] = useState(false);

  // state.storeDetails is /store/open/details, fetched ONCE app-wide in App.js and already
  // populated by the time the home page renders — so this bar costs no request. Never hardcode
  // the brand: this is a multi-tenant build and one deployment serves every store.
  const brand = useSelector((s) => s.storeDetails?.storeName) || BRAND_FALLBACK;

  return (
    <>
      {/* Structure copied from ShippingBar: the same flex row, gap-2, padding, type scale and
          bottom hairline. Only the ground colour and the copy differ. */}
      <div className="flex items-center justify-center gap-2 px-[14px] py-[9px] bg-[#F2F8F4] text-ink-900 text-[11.5px] font-[400] text-center border-b border-[#CFE3D6]">
        {/* ONE line of copy at every width — the spec's variant A, verbatim.
            An earlier revision swapped in a shorter line below `sm` to keep the bar to a single
            row on a 360px phone. That was dropped deliberately: the short line said "Verified
            silk. Verified zari" and named only two of the four parameters, so the ~99% of shoppers
            on phones never saw fabric or yarn — losing exactly the specificity the claim is built
            on. Two lines on a narrow phone is the accepted cost.
            No whitespace-nowrap, so it wraps rather than overflowing or truncating. This bar is in
            MyStore's ordinary flow, NOT inside PromoStack, so its height feeds no CSS variable and
            a wrap costs nothing downstream. */}
        <span>
          <Trans
            i18nKey="authPromiseBar.line"
            values={{ brand }}
            components={[
              <span aria-hidden="true" />,
              <span className="font-[500]" />,
            ]}
          />
        </span>

        <button
          type="button"
          onClick={() => setSheet(true)}
          aria-haspopup="dialog"
          aria-label={t("authPromiseBar.detailsLabel")}
          className="border-0 bg-transparent text-s-primary text-[14px] font-[500] leading-none p-0"
        >
          ⓘ
        </button>
      </div>

      <AuthenticityPromiseSheet
        sheet={sheet}
        setSheet={setSheet}
        brand={brand}
      />
    </>
  );
}
